import { useMemo } from 'react';
import { formatCurrency, formatPercent } from '../../utils/formatting';

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function sumCat(monthData, category) {
  if (!monthData?.[category]) return 0;
  return Object.values(monthData[category]).reduce((s, v) => s + (v || 0), 0);
}

function computeTotals(data) {
  if (!data?.months) return null;
  let revenue = 0, cogs = 0, expenses = 0;
  for (const mk of MONTH_KEYS) {
    revenue += sumCat(data.months[mk], 'income');
    cogs += sumCat(data.months[mk], 'cogs');
    expenses += sumCat(data.months[mk], 'expenses');
  }
  const grossProfit = revenue - cogs;
  const netIncome = grossProfit - expenses;
  return { revenue, cogs, expenses, grossProfit, netIncome };
}

const ALL_CARDS = [
  { key: 'global', label: 'Global', fullName: 'All Divisions', color: 'border-navy' },
  { key: 'bwm', label: 'BWM', fullName: 'Wealth Management', color: 'border-bullogic-blue' },
  { key: 'bts', label: 'BTS', fullName: 'Tax Services', color: 'border-deep-teal' },
  { key: 'bia', label: 'BIA', fullName: 'Insurance Agency', color: 'border-sky-blue' },
];

export default function DivisionComparison({ editableByClass, priorYearData, activeClass, onSelectClass }) {
  const globalTotals = useMemo(
    () => computeTotals(editableByClass.global),
    [editableByClass]
  );

  const metrics = useMemo(() => {
    return ALL_CARDS.map((div) => {
      const current = computeTotals(editableByClass[div.key]);
      // For prior year: global comes from allPLData, classes from priorYearData.
      // Won deals don't apply to prior years (they're current-year forward), so
      // pass undefined — prior year revenue stays pure historical.
      const priorData = div.key === 'global'
        ? (priorYearData?.global || null)
        : (priorYearData?.[div.key] || null);
      const prior = priorData ? computeTotals(priorData) : null;

      if (!current) return { ...div, current: null };

      const gpPct = current.revenue > 0 ? current.grossProfit / current.revenue : 0;
      const npPct = current.revenue > 0 ? current.netIncome / current.revenue : 0;
      const yoyGrowth = prior?.revenue ? (current.revenue - prior.revenue) / prior.revenue : null;

      return { ...div, current, gpPct, npPct, yoyGrowth };
    });
  }, [editableByClass, priorYearData]);

  return (
    <div className="grid grid-cols-4 gap-4 mb-5">
      {metrics.map((div) => {
        if (!div.current) return null;
        const isGlobal = div.key === 'global';
        const revenueShare = isGlobal ? 1 : (globalTotals?.revenue ? div.current.revenue / globalTotals.revenue : 0);
        const isPositiveNOI = div.current.netIncome >= 0;
        const isActive = activeClass === div.key;

        return (
          <button
            key={div.key}
            onClick={() => onSelectClass(div.key)}
            className={`bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4 text-left border-l-4 ${div.color} transition-all cursor-pointer ${
              isActive
                ? 'ring-2 ring-bullogic-blue/40 shadow-[0_4px_12px_rgba(0,0,0,0.12)]'
                : 'hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]'
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="text-[14px] font-bold text-navy">{div.label}</span>
                <span className="text-[11px] text-[#999] ml-2">{div.fullName}</span>
              </div>
              {div.yoyGrowth != null && (
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-[3px] ${
                  div.yoyGrowth >= 0
                    ? 'bg-deep-teal/10 text-deep-teal'
                    : 'bg-rose/10 text-rose'
                }`}>
                  {div.yoyGrowth >= 0 ? '+' : ''}{(div.yoyGrowth * 100).toFixed(1)}% YoY
                </span>
              )}
            </div>

            {/* Revenue */}
            <div className="mb-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999] mb-0.5">Revenue</div>
              <div className="flex items-baseline gap-2">
                <span className="text-[22px] font-bold text-navy">{formatCurrency(div.current.revenue)}</span>
                {!isGlobal && (
                  <span className="text-[11px] text-[#aaa]">{formatPercent(revenueShare)} of total</span>
                )}
              </div>
            </div>

            {/* Margin metrics */}
            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-100">
              <div>
                <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#999]">Gross Margin</div>
                <div className={`text-[15px] font-bold ${div.gpPct >= 0.40 ? 'text-deep-teal' : 'text-rose'}`}>
                  {formatPercent(div.gpPct)}
                </div>
              </div>
              <div>
                <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#999]">Net Margin</div>
                <div className={`text-[15px] font-bold ${isPositiveNOI ? 'text-deep-teal' : 'text-rose'}`}>
                  {formatPercent(div.npPct)}
                </div>
              </div>
              <div>
                <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#999]">Net Income</div>
                <div className={`text-[15px] font-bold ${isPositiveNOI ? 'text-deep-teal' : 'text-rose'}`}>
                  {formatCurrency(div.current.netIncome)}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
